//characters.ts

/**
 * Unicode Variation Selector 15 (VS15) forces text presentation for characters
 * that iOS/macOS may render as emoji by default.
 */
const VS15 = '\uFE0E';

/**
 * Characters in these Unicode ranges are commonly rendered as emoji on iOS.
 * Appending VS15 forces them into text (monochrome) presentation.
 */
const EMOJI_PRONE_RANGES: Array<[number, number]> = [
  [0x2600, 0x27bf], // Miscellaneous Symbols, Dingbats
  [0x2b50, 0x2b55], // Stars, circles
  [0x2934, 0x2935], // Arrows
  [0x25aa, 0x25ab], // Small squares
  [0x25fb, 0x25fe], // Medium squares
  [0x2614, 0x2615], // Umbrella, hot beverage
  [0x2648, 0x2653], // Zodiac signs
  [0x2660, 0x2668], // Card suits, hot springs
  [0x267b, 0x267b], // Recycling
  [0x267f, 0x267f], // Wheelchair
  [0x2693, 0x2693], // Anchor
  [0x2694, 0x2697], // Swords, alembic
  [0x2699, 0x2699], // Gear
  [0x269b, 0x269c], // Atom, fleur-de-lis
  [0x26a0, 0x26a1], // Warning, high voltage
  [0x26aa, 0x26ab], // Medium circles
  [0x26bd, 0x26be], // Soccer, baseball
  [0x26c4, 0x26c5], // Snowman, sun behind cloud
  [0x26ce, 0x26ce], // Ophiuchus
  [0x26d4, 0x26d4], // No entry
  [0x26ea, 0x26ea], // Church
  [0x26f2, 0x26f3], // Fountain, golf
  [0x26f5, 0x26f5], // Sailboat
  [0x26fa, 0x26fa], // Tent
  [0x26fd, 0x26fd], // Fuel pump
];

function isEmojiProne(codePoint: number): boolean {
  return EMOJI_PRONE_RANGES.some(
    ([start, end]) => codePoint >= start && codePoint <= end,
  );
}

/**
 * Appends VS15 (U+FE0E) to characters that iOS may render as color emoji,
 * forcing them into text (monochrome) presentation.
 * Safe characters are returned unchanged.
 */
export function ensureTextPresentation(char: string): string {
  if (!char) return char;
  const codePoint = char.codePointAt(0);
  if (codePoint === undefined) return char;
  if (char.includes(VS15)) return char;
  if (isEmojiProne(codePoint)) return char + VS15;
  return char;
}

export const characters = [
  // Stars Category
  { name: 'Black star', character: '★', categories: ['Stars', 'Shapes'] },
  { name: 'White star', character: '☆', categories: ['Stars', 'Shapes'] },
  { name: 'Star with shadow', character: '✪', categories: ['Stars', 'Shapes'] },
  { name: 'Star', character: '✰', categories: ['Stars', 'Shapes'] },

  // Currency Category
  { name: 'Dollar sign', character: '$', categories: ['Currency'] },
  { name: 'Cent sign', character: '¢', categories: ['Currency'] },
  { name: 'Pound sign', character: '£', categories: ['Currency'] },
  { name: 'Yen sign', character: '¥', categories: ['Currency'] },
  { name: 'Currency sign', character: '¤', categories: ['Currency'] },
  { name: 'New Sheqel sign', character: '₪', categories: ['Currency'] },
  { name: 'Euro sign', character: '€', categories: ['Currency'] },
  { name: 'Lira sign', character: '₤', categories: ['Currency'] },
  { name: 'Naira sign', character: '₦', categories: ['Currency'] },
  { name: 'Peseta sign', character: '₧', categories: ['Currency'] },
  { name: 'Rupee sign', character: '₨', categories: ['Currency'] },
  { name: 'Won sign', character: '₩', categories: ['Currency'] },
  { name: 'Tugrik sign', character: '₮', categories: ['Currency'] },
  { name: 'Peso sign', character: '₱', categories: ['Currency'] },
  { name: 'Guarani sign', character: '₲', categories: ['Currency'] },
  { name: 'Austral sign', character: '₳', categories: ['Currency'] },
  { name: 'Hryvnia sign', character: '₴', categories: ['Currency'] },
  { name: 'Cedi sign', character: '₵', categories: ['Currency'] },
  { name: 'Kip sign', character: '₭', categories: ['Currency'] },
  { name: 'Turkish Lira sign', character: '₺', categories: ['Currency'] },
  { name: 'Manat sign', character: '₼', categories: ['Currency'] },
  { name: 'Ruble sign', character: '₽', categories: ['Currency'] },
  { name: 'Lari sign', character: '₾', categories: ['Currency'] },

  // Basic symbols Category
  { name: 'Hash symbol', character: '#', categories: ['Basic symbols'] },
  { name: 'At symbol', character: '@', categories: ['Basic symbols'] },
  {
    name: 'Exclamation mark symbol',
    character: '!',
    categories: ['Basic symbols'],
  },
  { name: 'Percent symbol', character: '%', categories: ['Basic symbols'] },
  { name: 'Ampersand symbol', character: '&', categories: ['Basic symbols'] },
  { name: 'Asterisk symbol', character: '*', categories: ['Basic symbols'] },
  { name: 'Plus symbol', character: '+', categories: ['Basic symbols'] },
  { name: 'Minus symbol', character: '-', categories: ['Basic symbols'] },
  { name: 'Equals symbol', character: '=', categories: ['Basic symbols'] },
  { name: 'Caret symbol', character: '^', categories: ['Basic symbols'] },
  { name: 'Underscore symbol', character: '_', categories: ['Basic symbols'] },
  { name: 'Tilde symbol', character: '~', categories: ['Basic symbols'] },
  { name: 'Period symbol', character: '.', categories: ['Basic symbols'] },
  { name: 'Comma symbol', character: ',', categories: ['Basic symbols'] },
  { name: 'Colon symbol', character: ':', categories: ['Basic symbols'] },
  { name: 'Semicolon symbol', character: ';', categories: ['Basic symbols'] },
  {
    name: 'Double quote symbol',
    character: '"',
    categories: ['Basic symbols'],
  },
  {
    name: 'Single quote symbol',
    character: "'",
    categories: ['Basic symbols'],
  },
  { name: 'Backtick symbol', character: '`', categories: ['Basic symbols'] },

  // Punctuation
  { name: 'Question mark symbol', character: '?', categories: ['Punctuation'] },
  { name: 'Open parenthesis', character: '(', categories: ['Punctuation'] },
  { name: 'Close parenthesis', character: ')', categories: ['Punctuation'] },
  { name: 'Open bracket', character: '[', categories: ['Punctuation'] },
  { name: 'Close bracket', character: ']', categories: ['Punctuation'] },
  { name: 'Open brace', character: '{', categories: ['Punctuation'] },
  { name: 'Close brace', character: '}', categories: ['Punctuation'] },
  { name: 'Less than', character: '<', categories: ['Punctuation'] },
  { name: 'Greater than', character: '>', categories: ['Punctuation'] },
  { name: 'Slash', character: '/', categories: ['Punctuation'] },
  { name: 'Vertical bar', character: '|', categories: ['Punctuation'] },

  // Shapes
  {
    name: 'Black four-pointed star',
    character: '✦',
    categories: ['Stars', 'Shapes'],
  },
  {
    name: 'White four-pointed star',
    character: '✧',
    categories: ['Stars', 'Shapes'],
  },
  {
    name: 'Heavy white four-pointed star',
    character: '⭑',
    categories: ['Stars', 'Shapes'],
  },
  { name: 'Black diamond', character: '◆', categories: ['Shapes'] },
  { name: 'White diamond', character: '◇', categories: ['Shapes'] },
  { name: 'Fisheye', character: '◉', categories: ['Shapes'] },
  { name: 'White circle', character: '○', categories: ['Shapes'] },
  { name: 'Bullseye', character: '◎', categories: ['Shapes'] },
  { name: 'Black circle', character: '●', categories: ['Shapes'] },
  { name: 'Black square', character: '■', categories: ['Shapes'] },
  { name: 'White square', character: '□', categories: ['Shapes'] },
  {
    name: 'Black up-pointing triangle',
    character: '▲',
    categories: ['Shapes'],
  },
  {
    name: 'White up-pointing triangle',
    character: '△',
    categories: ['Shapes'],
  },
  {
    name: 'Black down-pointing triangle',
    character: '▼',
    categories: ['Shapes'],
  },
  {
    name: 'White down-pointing triangle',
    character: '▽',
    categories: ['Shapes'],
  },

  // Cards
  { name: 'Spade suit', character: '♠', categories: ['Cards'] },
  { name: 'Club suit', character: '♣', categories: ['Cards'] },
  { name: 'Heart suit', character: '♥', categories: ['Cards'] },
  { name: 'Diamond suit', character: '♦', categories: ['Cards'] },
  { name: 'White spade suit', character: '♤', categories: ['Cards'] },
  { name: 'White club suit', character: '♧', categories: ['Cards'] },
  { name: 'White heart suit', character: '♡', categories: ['Cards'] },
  { name: 'White diamond suit', character: '♢', categories: ['Cards'] },

  // Chess
  { name: 'White king', character: '♔', categories: ['Chess'] },
  { name: 'White queen', character: '♕', categories: ['Chess'] },
  { name: 'White rook', character: '♖', categories: ['Chess'] },
  { name: 'White bishop', character: '♗', categories: ['Chess'] },
  { name: 'White knight', character: '♘', categories: ['Chess'] },
  { name: 'White pawn', character: '♙', categories: ['Chess'] },
  { name: 'Black king', character: '♚', categories: ['Chess'] },
  { name: 'Black queen', character: '♛', categories: ['Chess'] },
  { name: 'Black rook', character: '♜', categories: ['Chess'] },
  { name: 'Black bishop', character: '♝', categories: ['Chess'] },
  { name: 'Black knight', character: '♞', categories: ['Chess'] },
  { name: 'Black pawn', character: '♟', categories: ['Chess'] },

  // Music
  { name: 'Quarter note', character: '♩', categories: ['Music'] },
  { name: 'Eighth note', character: '♪', categories: ['Music'] },
  { name: 'Beamed eighth notes', character: '♫', categories: ['Music'] },
  { name: 'Beamed sixteenth notes', character: '♬', categories: ['Music'] },
  { name: 'Music flat sign', character: '♭', categories: ['Music'] },
  { name: 'Music natural sign', character: '♮', categories: ['Music'] },
  { name: 'Music sharp sign', character: '♯', categories: ['Music'] },

  // Other
  { name: 'Recycle', character: '♺', categories: ['Other'] },
  {
    name: 'Universal recycling symbol',
    character: '♻',
    categories: ['Other'],
  },
  {
    name: 'Recycled paper symbol',
    character: '♼',
    categories: ['Other'],
  },
  {
    name: 'Partially recycled paper symbol',
    character: '♽',
    categories: ['Other'],
  },
  {
    name: 'Permanent paper sign',
    character: '♾',
    categories: ['Other'],
  },
  { name: 'Die face-1', character: '⚀', categories: ['Other'] },
  { name: 'Die face-2', character: '⚁', categories: ['Other'] },
  { name: 'Die face-3', character: '⚂', categories: ['Other'] },
  { name: 'Die face-4', character: '⚃', categories: ['Other'] },
  { name: 'Die face-5', character: '⚄', categories: ['Other'] },
  { name: 'Die face-6', character: '⚅', categories: ['Other'] },
  {
    name: 'White circle with dot right',
    character: '⚆',
    categories: ['Other'],
  },
  {
    name: 'Inverse white circle with dot right',
    character: '⚇',
    categories: ['Other'],
  },
  {
    name: 'White circle with two dots',
    character: '⚈',
    categories: ['Other'],
  },
  {
    name: 'Inverse white circle with two dots',
    character: '⚉',
    categories: ['Other'],
  },
  { name: 'Black flag', character: '⚐', categories: ['Other'] },
  { name: 'White flag', character: '⚑', categories: ['Other'] },
  { name: 'Hammer and pick', character: '⚒', categories: ['Other'] },
  { name: 'Anchor', character: '⚓', categories: ['Other'] },
  { name: 'Crossed swords', character: '⚔', categories: ['Other'] },
  { name: 'Medical symbol', character: '⚕', categories: ['Other'] },
  { name: 'Scales', character: '⚖', categories: ['Other'] },
  { name: 'Alembic', character: '⚗', categories: ['Other'] },
  { name: 'Flower', character: '⚘', categories: ['Other'] },
  { name: 'Gear', character: '⚙', categories: ['Other'] },
  { name: 'Staff of Hermes', character: '⚚', categories: ['Other'] },
  { name: 'Atom symbol', character: '⚛', categories: ['Other'] },
  { name: 'Fleur-de-lis', character: '⚜', categories: ['Other'] },
  { name: 'Outlined star', character: '⚝', categories: ['Stars', 'Other'] },
  {
    name: 'Three lines converging right',
    character: '⚞',
    categories: ['Other'],
  },
  {
    name: 'Three lines converging left',
    character: '⚟',
    categories: ['Other'],
  },
  { name: 'Warning', character: '⚠', categories: ['Other'] },
  {
    name: 'Doubled female sign',
    character: '⚢',
    categories: ['Other'],
  },
  { name: 'Doubled male sign', character: '⚣', categories: ['Other'] },
  {
    name: 'Interlocked male and female sign',
    character: '⚤',
    categories: ['Other'],
  },
  {
    name: 'Male and female sign',
    character: '⚥',
    categories: ['Other'],
  },
  {
    name: 'Male with stroke sign',
    character: '⚦',
    categories: ['Other'],
  },
  {
    name: 'Male with stroke and male and female sign',
    character: '⚧',
    categories: ['Other'],
  },
  {
    name: 'Vertical line with circle above',
    character: '⚨',
    categories: ['Other'],
  },
  {
    name: 'Horizontal male with stroke sign',
    character: '⚩',
    categories: ['Other'],
  },
  {
    name: 'Medium small white circle',
    character: '⚬',
    categories: ['Other'],
  },
  { name: 'Marriage symbol', character: '⚭', categories: ['Other'] },
  { name: 'Divorce symbol', character: '⚮', categories: ['Other'] },
  {
    name: 'Unmarried partnership symbol',
    character: '⚯',
    categories: ['Other'],
  },
  { name: 'Coffin', character: '⚰', categories: ['Other'] },
  { name: 'Funeral urn', character: '⚱', categories: ['Other'] },
  { name: 'Neuter', character: '⚲', categories: ['Other'] },
  { name: 'Earth', character: '⚳', categories: ['Other'] },
  { name: 'Earth retrograde', character: '⚴', categories: ['Other'] },
  { name: 'Ascelpius staff', character: '⚵', categories: ['Other'] },
  { name: 'Mercury', character: '⚶', categories: ['Other'] },
  { name: 'Mercury retrograde', character: '⚷', categories: ['Other'] },
  { name: 'Venus', character: '⚸', categories: ['Other'] },
  { name: 'Venus retrograde', character: '⚹', categories: ['Other'] },
  { name: 'Mars', character: '⚺', categories: ['Other'] },
  { name: 'Mars retrograde', character: '⚻', categories: ['Other'] },
  { name: 'Jupiter', character: '⚼', categories: ['Other'] },
  { name: 'Jupiter retrograde', character: '⚿', categories: ['Other'] },
  { name: 'Saturn', character: '⛀', categories: ['Other'] },
  { name: 'Saturn retrograde', character: '⛁', categories: ['Other'] },
  { name: 'Uranus', character: '⛂', categories: ['Other'] },
  { name: 'Uranus retrograde', character: '⛃', categories: ['Other'] },
  { name: 'Neptune', character: '⛆', categories: ['Other'] },
  { name: 'Neptune retrograde', character: '⛇', categories: ['Other'] },
  { name: 'Pluto', character: '⛈', categories: ['Other'] },
  { name: 'Pluto retrograde', character: '⛉', categories: ['Other'] },
  { name: 'Aries', character: '⛊', categories: ['Other'] },
  { name: 'Taurus', character: '⛋', categories: ['Other'] },
  { name: 'Gemini', character: '⛌', categories: ['Other'] },
  { name: 'Cancer', character: '⛍', categories: ['Other'] },
  { name: 'Leo', character: '⛏', categories: ['Other'] },
  { name: 'Virgo', character: '⛐', categories: ['Other'] },
  { name: 'Libra', character: '⛑', categories: ['Other'] },
  { name: 'Scorpio', character: '⛒', categories: ['Other'] },
  { name: 'Sagittarius', character: '⛓', categories: ['Other'] },
  { name: 'Capricorn', character: '⛕', categories: ['Other'] },
  { name: 'Aquarius', character: '⛖', categories: ['Other'] },
  { name: 'Pisces', character: '⛗', categories: ['Other'] },
  { name: 'Alpha', character: '⛘', categories: ['Other'] },
  { name: 'Beta', character: '⛙', categories: ['Other'] },
  { name: 'Gamma', character: '⛚', categories: ['Other'] },
  { name: 'Delta', character: '⛛', categories: ['Other'] },
  { name: 'Epsilon', character: '⛜', categories: ['Other'] },
  { name: 'Zeta', character: '⛝', categories: ['Other'] },
  { name: 'Eta', character: '⛞', categories: ['Other'] },
  { name: 'Theta', character: '⛟', categories: ['Other'] },
  { name: 'Iota', character: '⛠', categories: ['Other'] },
  { name: 'Kappa', character: '⛡', categories: ['Other'] },
  { name: 'Lambda', character: '⛢', categories: ['Other'] },
  { name: 'Mu', character: '⛣', categories: ['Other'] },
  { name: 'Nu', character: '⛤', categories: ['Stars', 'Other'] },
  { name: 'Xi', character: '⛥', categories: ['Stars', 'Other'] },
  { name: 'Omicron', character: '⛦', categories: ['Stars', 'Other'] },
  { name: 'Pi', character: '⛧', categories: ['Stars', 'Other'] },
  { name: 'Rho', character: '⛨', categories: ['Other'] },
  { name: 'Sigma', character: '⛩', categories: ['Other'] },
  { name: 'Tau', character: '⛫', categories: ['Other'] },
  { name: 'Upsilon', character: '⛬', categories: ['Other'] },
  { name: 'Phi', character: '⛭', categories: ['Other'] },
  { name: 'Chi', character: '⛮', categories: ['Other'] },
  { name: 'Psi', character: '⛯', categories: ['Other'] },
  { name: 'Omega', character: '⛰', categories: ['Other'] },
  { name: 'Open umbrella', character: '⛱', categories: ['Other'] },
  { name: 'Ferry', character: '⛴', categories: ['Other'] },
  {
    name: 'Square four corners',
    character: '⛶',
    categories: ['Other'],
  },
  { name: 'Skier', character: '⛷', categories: ['Other'] },
  { name: 'Ice skate', character: '⛸', categories: ['Other'] },
  { name: 'Carousel horse', character: '⛻', categories: ['Other'] },
  {
    name: 'Triangle flag on post',
    character: '⛼',
    categories: ['Other'],
  },
  { name: 'Japanese lantern', character: '⛾', categories: ['Other'] },
  {
    name: 'Triangle with rounded corners',
    character: '⛿',
    categories: ['Other'],
  },

  // Other
  { name: 'Copyright', character: '©', categories: ['Other'] },
  { name: 'Registered', character: '®', categories: ['Other'] },
  { name: 'Trademark', character: '™', categories: ['Other'] },
  { name: 'Section', character: '§', categories: ['Other'] },
  { name: 'Pilcrow', character: '¶', categories: ['Other'] },
  { name: 'Bullet', character: '•', categories: ['Other'] },
  { name: 'Dagger', character: '†', categories: ['Other'] },
  { name: 'Double dagger', character: '‡', categories: ['Other'] },
  { name: 'Degree', character: '°', categories: ['Other'] },
  { name: 'Plus-minus', character: '±', categories: ['Other'] },
  { name: 'Micro', character: 'µ', categories: ['Other'] },
  { name: 'Double vertical line', character: '‖', categories: ['Other'] },
  { name: 'Half', character: '½', categories: ['Other'] },
  { name: 'Quarter', character: '¼', categories: ['Other'] },
  { name: 'Three quarters', character: '¾', categories: ['Other'] },
  { name: 'Superscript two', character: '²', categories: ['Other'] },
  { name: 'Superscript three', character: '³', categories: ['Other'] },
  { name: 'Subscript one', character: '₁', categories: ['Other'] },
  { name: 'Subscript two', character: '₂', categories: ['Other'] },
  { name: 'Subscript three', character: '₃', categories: ['Other'] },
  { name: 'Account of', character: '℀', categories: ['Other'] },
  { name: 'Addressed to the subject', character: '℁', categories: ['Other'] },
  { name: 'Double-struck capital C', character: 'ℂ', categories: ['Other'] },
  { name: 'Care of', character: '℅', categories: ['Other'] },
  { name: 'Cada una', character: '℆', categories: ['Other'] },
  { name: 'Euler constant', character: 'ℇ', categories: ['Other'] },
  { name: 'Fahrenheit', character: '℉', categories: ['Other'] },
  { name: 'Script small g', character: 'ℊ', categories: ['Other'] },
  { name: 'Script capital H', character: 'ℋ', categories: ['Other'] },
  { name: 'Black-letter capital H', character: 'ℌ', categories: ['Other'] },
  { name: 'Double-struck capital H', character: 'ℍ', categories: ['Other'] },
  { name: 'Planck constant', character: 'ℎ', categories: ['Other'] },
  { name: 'Reduced Planck constant', character: 'ℏ', categories: ['Other'] },
  { name: 'Script capital I', character: 'ℐ', categories: ['Other'] },
  { name: 'Black-letter capital I', character: 'ℑ', categories: ['Other'] },
  { name: 'Script capital L', character: 'ℒ', categories: ['Other'] },
  { name: 'Script small L', character: 'ℓ', categories: ['Other'] },
  { name: 'L b Bar', character: '℔', categories: ['Other'] },
  { name: 'Double-struck capital N', character: 'ℕ', categories: ['Other'] },
  { name: 'Numero sign', character: '№', categories: ['Other'] },
  { name: 'Sound recording copyright', character: '℗', categories: ['Other'] },
  { name: 'Double-struck capital P', character: 'ℙ', categories: ['Other'] },
  { name: 'Double-struck capital Q', character: 'ℚ', categories: ['Other'] },
  { name: 'Double-struck capital R', character: 'ℛ', categories: ['Other'] },
  { name: 'Black-letter capital R', character: 'ℜ', categories: ['Other'] },
  { name: 'Double-struck capital R', character: 'ℝ', categories: ['Other'] },
  { name: 'Prescription', character: '℞', categories: ['Other'] },
  { name: 'Response', character: '℟', categories: ['Other'] },
  { name: 'Service mark', character: '℠', categories: ['Other'] },
  { name: 'Telephone sign', character: '℡', categories: ['Other'] },
  { name: 'Versicle', character: '℣', categories: ['Other'] },
  { name: 'Double-struck capital Z', character: 'ℤ', categories: ['Other'] },
  { name: 'Ounce', character: '℥', categories: ['Other'] },
  { name: 'Ohm sign', character: 'Ω', categories: ['Other'] },
  { name: 'Inverted ohm sign', character: '℧', categories: ['Other'] },
  { name: 'Script capital Z', character: 'ℨ', categories: ['Other'] },
  {
    name: 'Turned Greek small letter iota',
    character: '℩',
    categories: ['Other'],
  },
  { name: 'Kelvin', character: 'K', categories: ['Other'] },
  { name: 'Angstrom sign', character: 'Å', categories: ['Other'] },
  { name: 'Script capital B', character: 'ℬ', categories: ['Other'] },
  { name: 'Black-letter capital C', character: 'ℭ', categories: ['Other'] },
  { name: 'Estimated', character: '℮', categories: ['Other'] },
  { name: 'Script small e', character: 'ℯ', categories: ['Other'] },
  { name: 'Script capital E', character: 'ℰ', categories: ['Other'] },
  { name: 'Script capital F', character: 'ℱ', categories: ['Other'] },
  { name: 'Turned capital F', character: 'Ⅎ', categories: ['Other'] },
  { name: 'Script capital M', character: 'ℳ', categories: ['Other'] },
  { name: 'Script small o', character: 'ℴ', categories: ['Other'] },
  { name: 'Alef symbol', character: 'ℵ', categories: ['Other'] },
  { name: 'Bet symbol', character: 'ℶ', categories: ['Other'] },
  { name: 'Gimel symbol', character: 'ℷ', categories: ['Other'] },
  { name: 'Dalet symbol', character: 'ℸ', categories: ['Other'] },
  { name: 'Information source', character: 'ℹ', categories: ['Other'] },
  { name: 'Turned capital Q', character: '℺', categories: ['Other'] },
  { name: 'Facsimile sign', character: '℻', categories: ['Other'] },
  { name: 'Pi', character: 'ℼ', categories: ['Other'] },
  { name: 'Gamma', character: 'ℽ', categories: ['Other'] },
  { name: 'Gamma', character: 'ℾ', categories: ['Other'] },
  { name: 'Capital pi', character: 'ℿ', categories: ['Other'] },
  { name: 'Double-struck capital S', character: '⅀', categories: ['Other'] },
  {
    name: 'Turned sans-serif capital G',
    character: '⅁',
    categories: ['Other'],
  },
  { name: 'Sans-serif capital L', character: '⅂', categories: ['Other'] },
  {
    name: 'Reversed sans-serif capital L',
    character: '⅃',
    categories: ['Other'],
  },
  {
    name: 'Turned sans-serif capital Y',
    character: '⅄',
    categories: ['Other'],
  },
  { name: 'Double-struck capital D', character: 'ⅅ', categories: ['Other'] },
  { name: 'Double-struck small d', character: 'ⅆ', categories: ['Other'] },
  { name: 'Double-struck small e', character: 'ⅇ', categories: ['Other'] },
  { name: 'Double-struck small i', character: 'ⅈ', categories: ['Other'] },
  { name: 'Double-struck small j', character: 'ⅉ', categories: ['Other'] },
  { name: 'Property line', character: '⅊', categories: ['Other'] },
  { name: 'Turned ampersand', character: '⅋', categories: ['Other'] },
  { name: 'Property line', character: '⅌', categories: ['Other'] },
  { name: 'Script capital I', character: '⅍', categories: ['Other'] },
  { name: 'Inverted capital N', character: 'ⅎ', categories: ['Other'] },
  { name: 'Roman numeral one', character: 'Ⅰ', categories: ['Other'] },
  { name: 'Roman numeral two', character: 'Ⅱ', categories: ['Other'] },
  { name: 'Roman numeral three', character: 'Ⅲ', categories: ['Other'] },
  { name: 'Roman numeral four', character: 'Ⅳ', categories: ['Other'] },
  { name: 'Roman numeral five', character: 'Ⅴ', categories: ['Other'] },
  { name: 'Roman numeral six', character: 'Ⅵ', categories: ['Other'] },
  { name: 'Roman numeral seven', character: 'Ⅶ', categories: ['Other'] },
  { name: 'Roman numeral eight', character: 'Ⅷ', categories: ['Other'] },
  { name: 'Roman numeral nine', character: 'Ⅸ', categories: ['Other'] },
  { name: 'Roman numeral ten', character: 'Ⅹ', categories: ['Other'] },
  { name: 'Roman numeral eleven', character: 'Ⅺ', categories: ['Other'] },
  { name: 'Roman numeral twelve', character: 'Ⅻ', categories: ['Other'] },
  { name: 'Roman numeral fifty', character: 'Ⅼ', categories: ['Other'] },
  { name: 'Roman numeral one hundred', character: 'Ⅽ', categories: ['Other'] },
  {
    name: 'Roman numeral five hundred',
    character: 'Ⅾ',
    categories: ['Other'],
  },
  {
    name: 'Roman numeral one thousand',
    character: 'Ⅿ',
    categories: ['Other'],
  },
];
